import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import PropTypes from 'prop-types';
import { GoogleMap, InfoWindow, Marker } from '@react-google-maps/api';
import { faCheck, faExclamation } from '@fortawesome/pro-solid-svg-icons';
import useGoogleMapsLoader from './useGoogleMapsLoader';
import { getPublicDashboardToken } from '../../services/dashboardApi';
import HasRolePrivileges from '../../../highorder/HasRolePrivileges';
import { RaaRoles } from '../../../../utility/SecurityRules';

const DEFAULT_CENTER = { lat: 39.5, lng: -98.35 }; // rough center of the US
const DEFAULT_ZOOM = 4;

const buildMarkerIcon = (device) => {
  const hasTriggeredAlert = device.triggeredAlertCount > 0;
  const icon = hasTriggeredAlert ? faExclamation : faCheck;
  const [iconWidth, iconHeight, , , path] = icon.icon;
  const color = hasTriggeredAlert ? '#d9534f' : '#5cb85c';

  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32">
      <circle cx="16" cy="16" r="15" fill="${color}" stroke="white" stroke-width="2"/>
      <svg x="9" y="9" width="14" height="14" viewBox="0 0 ${iconWidth} ${iconHeight}" preserveAspectRatio="xMidYMid meet">
        <path fill="white" d="${Array.isArray(path) ? path.join(' ') : path}"/>
      </svg>
    </svg>
  `;

  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    scaledSize: new window.google.maps.Size(32, 32),
    anchor: new window.google.maps.Point(16, 16),
  };
};

const formatDeviceStatus = (status) => {
  if (!status) return 'Unknown';
  return status.charAt(0).toUpperCase() + status.slice(1);
};

const getDeviceIdentifier = (device) => device.deviceKey || device.macAddress || device.mac || '';

const getLastMessage = (device) => (
  device.lastMessageAt
  || device.lastMessage
  || device.lastCommunication
  || device.updatedAt
  || ''
);

function DeviceMap({
  devices, height, focusedDeviceId, showEmptyStateCta,
}) {
  // useJsApiLoader handles script injection, deduplication, and readiness.

  const { isLoaded, loadError } = useGoogleMapsLoader();
  const [map, setMap] = useState(null);
  const [selectedDeviceId, setSelectedDeviceId] = useState(null);
  const isPublicView = !!getPublicDashboardToken();

  const geocodedDevices = useMemo(
    () => devices
      .filter((d) => d.geocode)
      .map((d) => ({
        ...d,
        geocode: {
          latitude: Number(d.geocode.latitude),
          longitude: Number(d.geocode.longitude),
        },
      }))
      .filter((d) => Number.isFinite(d.geocode.latitude)
        && Number.isFinite(d.geocode.longitude)),
    [devices],
  );

  const onMapLoad = useCallback((mapInstance) => {
    setMap(mapInstance);
  }, []);

  const onMapUnmount = useCallback(() => {
    setMap(null);
  }, []);

  // Auto-fit bounds to all visible devices whenever the list changes.
  useEffect(() => {
    if (!map || geocodedDevices.length === 0) return;
    const bounds = new window.google.maps.LatLngBounds();
    geocodedDevices.forEach((d) => {
      bounds.extend({ lat: d.geocode.latitude, lng: d.geocode.longitude });
    });
    map.fitBounds(bounds);
    // Prevent extreme zoom when there's only one marker.
    if (geocodedDevices.length === 1) {
      window.google.maps.event.addListenerOnce(map, 'idle', () => {
        if (map.getZoom() > 12) map.setZoom(12);
      });
    }
  }, [map, geocodedDevices]);

  // Pan to a device when the parent asks us to focus it.
  useEffect(() => {
    if (!map || focusedDeviceId == null) return;
    const device = geocodedDevices.find((d) => d.id === focusedDeviceId);
    if (!device) return;
    map.panTo({ lat: device.geocode.latitude, lng: device.geocode.longitude });
    if (map.getZoom() < 12) map.setZoom(12);
    setSelectedDeviceId(device.id);
  }, [map, focusedDeviceId, geocodedDevices]);

  if (loadError) {
    return (
      <div className="alert alert-warning" role="alert">
        Unable to load the map. Please try again later.
      </div>
    );
  }

  if (!isLoaded) {
    return <div style={{ width: '100%', height }} className="bg-light" />;
  }

  const selectedDevice = geocodedDevices.find((d) => d.id === selectedDeviceId);

  return (
    <div style={{
      position: 'relative', width: '100%', height,
    }}
    >
      <GoogleMap
        mapContainerStyle={{ width: '100%', height: '100%' }}
        center={DEFAULT_CENTER}
        zoom={DEFAULT_ZOOM}
        onLoad={onMapLoad}
        onUnmount={onMapUnmount}
        options={{
          mapTypeControl: true,
          mapTypeControlOptions: {
            style: window.google.maps.MapTypeControlStyle.HORIZONTAL_BAR,
            position: window.google.maps.ControlPosition.TOP_RIGHT,
            mapTypeIds: [
              window.google.maps.MapTypeId.ROADMAP,
              window.google.maps.MapTypeId.SATELLITE,
            ],
          },
          streetViewControl: false,
          fullscreenControl: false,
        }}
      >
        {geocodedDevices.map((device) => (
          <Marker
            key={device.id}
            position={{ lat: device.geocode.latitude, lng: device.geocode.longitude }}
            title={device.name || ''}
            icon={buildMarkerIcon(device)}
            onClick={() => setSelectedDeviceId(device.id)}
          />
        ))}

        {selectedDevice && (
          <InfoWindow
            position={{
              lat: selectedDevice.geocode.latitude,
              lng: selectedDevice.geocode.longitude,
            }}
            onCloseClick={() => setSelectedDeviceId(null)}
          >
            <div style={{
              minWidth: 260,
              color: '#555',
              fontSize: 14,
              lineHeight: 1.15,
            }}
            >
              <div style={{ marginBottom: 8 }}>
                <a
                  href={`/s/devices/${selectedDevice.id}`}
                  style={{
                    color: '#337ab7',
                    fontSize: 22,
                    textDecoration: 'underline',
                  }}
                >
                  {selectedDevice.name}
                  {getDeviceIdentifier(selectedDevice) && ` - ${getDeviceIdentifier(selectedDevice)}`}
                </a>
              </div>

              <div>
                Type:
                {' '}
                <b>{selectedDevice.type || 'Unknown'}</b>
              </div>

              <div style={{ color: '#5bc0de' }}>
                Alerts Configured:
                {' '}
                <b>{selectedDevice.alertCount || selectedDevice.configuredAlertCount || 0}</b>
              </div>

              <div style={{ color: '#5bc0de' }}>
                Alerts Triggered:
                {' '}
                <b>{selectedDevice.triggeredAlertCount || 0}</b>
              </div>

              <div>
                Connection:
                {' '}
                <b>{formatDeviceStatus(selectedDevice.deviceStatus)}</b>
              </div>

              <div>
                Last Message:
                {' '}
                <b>{getLastMessage(selectedDevice) || 'N/A'}</b>
              </div>

              <div>
                ID/MAC:
                {' '}
                <b>{getDeviceIdentifier(selectedDevice) || 'N/A'}</b>
              </div>

              <div>
                IP Address:
                {' '}
                <b>{selectedDevice.ipAddress || selectedDevice.ip || 'N/A'}</b>
              </div>
            </div>
          </InfoWindow>
        )}
      </GoogleMap>

      {geocodedDevices.length === 0 && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
          }}
        >
          <div
            style={{
              maxWidth: 320,
              padding: '24px 28px',
              textAlign: 'center',
              background: 'rgba(255, 255, 255, 0.95)',
              borderRadius: 4,
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
              pointerEvents: 'auto',
            }}
          >
            <div
              style={{
                marginBottom: 16,
                color: '#555',
                fontSize: 16,
              }}
            >
              Visualize all your devices across locations
            </div>
            {showEmptyStateCta && !isPublicView && (
              <HasRolePrivileges targetRoleList={[RaaRoles.MANAGER, RaaRoles.ADMIN]}>
                <a href="/devices/device-map#device-list" className="btn btn-primary">
                  Plot Devices
                </a>
              </HasRolePrivileges>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

DeviceMap.propTypes = {
  devices: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string,
    type: PropTypes.string,
    deviceKey: PropTypes.string,
    deviceStatus: PropTypes.string,
    triggeredAlertCount: PropTypes.number,
    geocode: PropTypes.shape({
      latitude: PropTypes.number,
      longitude: PropTypes.number,
    }),
  })).isRequired,
  height: PropTypes.string,
  focusedDeviceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  showEmptyStateCta: PropTypes.bool,
};

DeviceMap.defaultProps = {
  height: '100%',
  focusedDeviceId: null,
  showEmptyStateCta: true,
};

export default DeviceMap;
